import React, { useCallback, useMemo } from 'react';
import { Row } from '@ydistri/ds';
import StatisticsItemComponent from '../../../../../../../../ProjectAdministration/common/validation-results/StatisticsItemComponent';
import {
  StatisticsItem,
  StatisticsItemType,
} from '../../../../../../../../ProjectAdministration/common/administrationItemsTypes';
import { ImportedItemRow } from '../../../../../../../../../lib/imports/importTypes';

interface ParsedItemStatsProps<T> {
  items: ImportedItemRow<T>[];
  errorsCount: number;
  onItemSelected?: (item: StatisticsItem | null) => void;
  selectedItemType?: ParsedItemStatsType | null;
}

export enum ParsedItemStatsType {
  VALID = 'Valid',
  ERROR = 'Errors',
  TOTAL = 'Total',
}

/**
 * Displays a number of valid, error and total items in the parsed items list.
 * @param items
 * @param errorsCount
 * @param onItemSelected When an item is selected, this callback is called with the selected item or null if the item is deselected.
 * @param selectedItemType
 * @constructor
 */
const ParsedItemStats = <T extends object>({
  items,
  errorsCount,
  onItemSelected,
  selectedItemType,
}: ParsedItemStatsProps<T>): React.ReactElement => {
  const statsItems: StatisticsItem[] = useMemo(() => {
    return [
      {
        label: ParsedItemStatsType.VALID,
        observation: items.length - errorsCount,
        type: StatisticsItemType.SUCCESS,
      },
      {
        label: ParsedItemStatsType.ERROR,
        observation: errorsCount,
        type: StatisticsItemType.ERROR,
      },
      {
        label: ParsedItemStatsType.TOTAL,
        observation: items.length,
        type: StatisticsItemType.UNCHANGED,
      },
    ];
  }, [items.length, errorsCount]);

  const statsItemClickHandler = useCallback(
    (item: StatisticsItem) => {
      if (item.label === selectedItemType?.toString()) {
        if (onItemSelected) {
          onItemSelected(null);
        }
      } else {
        if (onItemSelected) {
          onItemSelected(item);
        }
      }
    },
    [onItemSelected, selectedItemType],
  );

  return (
    <Row $gap="2rem">
      {statsItems.map(item => {
        return (
          <StatisticsItemComponent
            key={item.label}
            item={item}
            data-testid={`StatisticsItemComponent-${item.label}`}
            onClick={item.observation > 0 ? statsItemClickHandler : undefined}
            selected={selectedItemType?.toString() === item.label}
          />
        );
      })}
    </Row>
  );
};

export default ParsedItemStats;
